// HTTP client wrapper for `/api/secure/me/...` patient-portal endpoints.
//
// Reuses the project's apiURL + commonHeadersWeb so existing axios interceptors
// (401 handling, etc.) apply automatically. The patient-portal-specific value-add
// is the `patientId` query parameter — added on every call so the server-side
// PatientContextInterceptor can resolve the profile (PLAN §3.3, §5).
//
// `requirePatientContext: false` (default for profile listing/creation) skips
// attaching the param — the backend interceptor's PROFILE_AGNOSTIC_SUFFIXES
// list also skips validation for the matching paths.

import axios from 'axios'
import {
  apiURL,
  commonHeadersWeb,
  platformWeb,
  getCountryCode,
  getMainLanguageCode,
} from 'src/consumer/services'

const CURRENT_PROFILE_KEY = 'patientPortal.currentProfileId'

function readCurrentProfileId() {
  try {
    return localStorage.getItem(CURRENT_PROFILE_KEY) || null
  } catch (e) {
    return null
  }
}

function readToken() {
  try {
    return localStorage.getItem('token') || null
  } catch (e) {
    return null
  }
}

function buildHeaders({ extra = {} } = {}) {
  const token = readToken()
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  return {
    ...commonHeadersWeb,
    'ef-user-country-code': countryCode,
    'ef-app-language': mainLanguageCode,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...extra,
  }
}

function buildParams({
  requirePatientContext = false,
  profileId: explicitProfileId,
  params: extra = {},
} = {}) {
  const params = {
    languageCode: getMainLanguageCode(),
    platform: platformWeb,
    countryCode: getCountryCode(),
    ...extra,
  }
  if (requirePatientContext) {
    // Prefer the caller-supplied profileId (sourced from React state via
    // PatientContext) over localStorage. localStorage is the persistence
    // layer for "last selected profile" but it can race with the React
    // render that's actually firing this request — and a single user can
    // own multiple PatientInfo records (one per clinic brand), so the
    // server NEEDS this param to disambiguate which patient identity the
    // request is acting on. Falling back to localStorage covers older
    // callers that haven't been threaded with the explicit option yet.
    const patientId = explicitProfileId || readCurrentProfileId()
    if (patientId) {
      params.patientId = patientId
    } else if (typeof console !== 'undefined') {
      // Surface the misconfiguration loudly so the next request that
      // returns 400 from the patient-portal interceptor is easy to trace.
      console.warn(
        '[patientApi] requirePatientContext=true but no patientId available — request will 400.',
      )
    }
  }
  return params
}

export async function patientGet(path, options = {}) {
  // Forward any extra axios config the caller passes — primarily so PDF /
  // image downloads can set responseType:'blob' without us needing a
  // dedicated wrapper. Anything not recognised by buildHeaders / buildParams
  // is treated as raw axios config (timeout, signal, onDownloadProgress…).
  const { extraAxiosConfig, responseType, signal, timeout, onDownloadProgress } = options
  return axios.get(`${apiURL}${path}`, {
    headers: buildHeaders(options),
    params: buildParams(options),
    ...(responseType ? { responseType } : {}),
    ...(signal ? { signal } : {}),
    ...(timeout ? { timeout } : {}),
    ...(onDownloadProgress ? { onDownloadProgress } : {}),
    ...(extraAxiosConfig || {}),
  })
}

export async function patientPost(path, body, options = {}) {
  return axios.post(`${apiURL}${path}`, body, {
    headers: buildHeaders(options),
    params: buildParams(options),
  })
}

export async function patientPatch(path, body, options = {}) {
  return axios.patch(`${apiURL}${path}`, body, {
    headers: buildHeaders(options),
    params: buildParams(options),
  })
}

export async function patientDelete(path, options = {}) {
  return axios.delete(`${apiURL}${path}`, {
    headers: buildHeaders(options),
    params: buildParams(options),
  })
}
